// src/pages/CampaignsPage.js
import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Tooltip, Checkbox, Chip, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Campaign as CampaignIcon, People as PeopleIcon
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { ApiContext } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';

export default function CampaignsPage() {
  const { campaigns, agents } = useContext(ApiContext);
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { register, handleSubmit, reset, formState:{ errors } } = useForm();

  // Assignment dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCampaign, setAssignCampaign] = useState(null);
  const [allAgents, setAllAgents] = useState([]);
  const [assignedAgents, setAssignedAgents] = useState({}); // { agentId: true/false }
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);

  const load = async () => {
    try {
      const r = await campaigns.getAll();
      setList(r?.data?.campaigns ?? []);
    } catch (e) { console.error(e); showError('Error al cargar'); }
  };

  useEffect(() => { load(); }, []);

  const openDialog = (row = null) => {
    setEditing(row);
    reset({
      descripcion: row?.descripcion || '',
      company: row?.company || ''
    });
    setOpen(true);
  };

  const onSubmit = async (f) => {
    try {
      if (editing) {
        await campaigns.update(editing.idcampaign, f);
        showSuccess('Campaña actualizada');
      } else {
        await campaigns.create(f);
        showSuccess('Campaña creada');
      }
      setOpen(false); setEditing(null);
      load();
    } catch (e) { console.error(e); showError('Error al guardar'); }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`¿Eliminar campaña "${row.descripcion}"?`)) return;
    try {
      await campaigns.delete(row.idcampaign);
      showSuccess('Campaña eliminada'); load();
    } catch (e) { console.error(e); showError('Error al eliminar'); }
  };

  // ============================================================
  // Asignación de usuarios a campaña
  // ============================================================
  const openAssignDialog = async (campaign) => {
    setAssignCampaign(campaign);
    setAssignLoading(true);
    setAssignOpen(true);

    try {
      const [agentsRes, assignmentsRes] = await Promise.all([
        agents.getAll(),
        campaigns.getAssignments()
      ]);

      const agentList = agentsRes?.data?.agents || agentsRes?.data || [];
      setAllAgents(Array.isArray(agentList) ? agentList : []);

      // Construir mapa de agentes asignados a ESTA campaña
      const assignments = assignmentsRes?.data?.assignments || [];
      const assigned = {};
      assignments
        .filter(a => a.idcampaign === campaign.idcampaign)
        .forEach(a => { assigned[a.agent] = true; });
      setAssignedAgents(assigned);
    } catch (e) {
      console.error(e);
      showError('Error al cargar asignaciones');
    } finally {
      setAssignLoading(false);
    }
  };

  const toggleAgentAssignment = (agentId) => {
    setAssignedAgents(prev => ({
      ...prev,
      [agentId]: !prev[agentId]
    }));
  };

  const saveAssignments = async () => {
    if (!assignCampaign) return;
    setAssignSaving(true);

    try {
      // Para cada agente que cambió, actualizar sus campañas
      // Necesitamos obtener las campañas actuales de cada agente afectado y actualizar
      const agentsToUpdate = Object.keys(assignedAgents);

      for (const agentId of agentsToUpdate) {
        // Obtener campañas actuales del agente
        let currentRes;
        try {
          currentRes = await campaigns.getAgentAssignments(agentId);
        } catch {
          currentRes = { data: { campaigns: [] } };
        }
        const currentCampaigns = (currentRes?.data?.campaigns || []).map(c => c.idcampaign);

        let newCampaigns;
        if (assignedAgents[agentId]) {
          // Agregar esta campaña si no está
          newCampaigns = currentCampaigns.includes(assignCampaign.idcampaign)
            ? currentCampaigns
            : [...currentCampaigns, assignCampaign.idcampaign];
        } else {
          // Quitar esta campaña
          newCampaigns = currentCampaigns.filter(id => id !== assignCampaign.idcampaign);
        }

        await campaigns.setAgentAssignments(agentId, newCampaigns);
      }

      showSuccess('Asignaciones guardadas');
      setAssignOpen(false);
    } catch (e) {
      console.error(e);
      showError('Error al guardar asignaciones');
    } finally {
      setAssignSaving(false);
    }
  };

  const assignedCount = Object.values(assignedAgents).filter(Boolean).length;

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>Campañas</Typography>
        {canAction('create_campaign') && (
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => openDialog(null)}>
            Nueva Campaña
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <CampaignIcon fontSize="small" />
            <Typography variant="body2">Total: {list.length}</Typography>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row) => (
              <TableRow key={row.idcampaign} hover>
                <TableCell>{row.idcampaign}</TableCell>
                <TableCell>{row.descripcion}</TableCell>
                <TableCell>{row.company || '-'}</TableCell>
                <TableCell align="right">
                  {canAction('assign_users_campaign') && (
                    <Tooltip title="Asignar Usuarios">
                      <IconButton size="small" color="primary" onClick={() => openAssignDialog(row)}>
                        <PeopleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canAction('edit_campaign') && (
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openDialog(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canAction('delete_campaign') && (
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => onDelete(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center">No hay campañas</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Crear/Editar Campaña */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar Campaña' : 'Nueva Campaña'}</DialogTitle>
        <DialogContent dividers>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Descripción"
              {...register('descripcion', { required: 'Requerido' })}
              error={!!errors.descripcion}
              helperText={errors.descripcion?.message}
            />
            <TextField label="Empresa" {...register('company')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            {editing ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Asignar Usuarios a Campaña */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Asignar Usuarios a: {assignCampaign?.descripcion}
        </DialogTitle>
        <DialogContent dividers>
          {assignLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Seleccione los usuarios que tendrán acceso a esta campaña.
                Los usuarios sin campañas asignadas ven todas.
              </Typography>
              <Chip
                label={`${assignedCount} usuario${assignedCount !== 1 ? 's' : ''} asignado${assignedCount !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
                sx={{ mb: 2 }}
              />
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={allAgents.length > 0 && allAgents.every(a => assignedAgents[a.agent])}
                          indeterminate={assignedCount > 0 && assignedCount < allAgents.length}
                          onChange={() => {
                            const allChecked = allAgents.every(a => assignedAgents[a.agent]);
                            const next = {};
                            allAgents.forEach(a => { next[a.agent] = !allChecked; });
                            setAssignedAgents(next);
                          }}
                        />
                      </TableCell>
                      <TableCell>Agente</TableCell>
                      <TableCell>Nombre</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allAgents.map((agent) => (
                      <TableRow
                        key={agent.agent}
                        hover
                        onClick={() => toggleAgentAssignment(agent.agent)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={!!assignedAgents[agent.agent]} />
                        </TableCell>
                        <TableCell>{agent.agent}</TableCell>
                        <TableCell>{agent.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={saveAssignments}
            disabled={assignSaving || assignLoading}
          >
            {assignSaving ? 'Guardando...' : 'Guardar Asignaciones'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
